// src/pages/Recursos.jsx
// Simulador de Validación Lean - Ticket 1: Motor de flujo y reglas de habilitación
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Target, FlaskConical, BarChart3, AlertTriangle } from 'lucide-react';
import Navbar from '../components/Navbar';
import LeanFlowSimulator from '../components/LeanFlowSimulator';
import ShareButton from '../components/ShareButton';

export default function Recursos() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Simulador para Validar tu Startup",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Herramienta interactiva gratuita para guiar la validación de startups siguiendo metodología Lean Startup. Descubre qué validar primero, define experimentos y métricas clave, y evita construir productos sin demanda real.",
    "featureList": [
      "Flujo guiado de validación paso a paso",
      "Recomendaciones de experimentos Lean",
      "Definición de métricas y umbrales",
      "Identificación de antipatrones",
      "Ejemplos reales por industria",
      "Guardado automático de progreso"
    ],
    "author": {
      "@type": "Person",
      "name": "David Prudencio"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Edutech Pulse Lab",
      "url": "https://edutechpulselab.com"
    }
  };

  return (
    <>
      <Helmet>
        <title>Simulador para Validar tu Startup Gratis | Metodología Lean | Edutech Pulse Lab</title>
        <meta name="description" content="Herramienta gratuita para validar tu startup con metodología Lean. Descubre qué validar primero, define experimentos MVP, métricas clave y evita construir en falso. Guía paso a paso con ejemplos reales." />
        <meta name="keywords" content="validar startup, lean startup, MVP, validación de hipótesis, experimentos lean, métricas startup, problem-solution fit, product-market fit, customer development, validación de mercado, startup validation, simulador lean, herramienta startup gratis" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Simulador para Validar tu Startup - Metodología Lean Paso a Paso" />
        <meta property="og:description" content="Descubre qué debes validar primero y evita construir en falso. Herramienta interactiva gratuita con metodología Lean Startup, ejemplos reales y métricas clave." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://edutechpulselab.com/recursos/lean-simulador" />
        <meta property="og:image" content="https://edutechpulselab.com/OG_GRAFICOS/OG_recurso_LeanStartUP.jpeg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Simulador para Validar tu Startup | Lean Startup" />
        <meta name="twitter:description" content="Herramienta interactiva gratuita: descubre qué validar primero, define experimentos MVP y métricas clave. Evita construir productos sin demanda." />
        <meta name="twitter:image" content="https://edutechpulselab.com/OG_GRAFICOS/OG_recurso_LeanStartUP.jpeg" />
        
        {/* Canonical */}
        <link rel="canonical" href="https://edutechpulselab.com/recursos/lean-simulador" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">

        {/* Breadcrumb */}
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 py-3">
            <Link to="/recursos" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors">
              <ArrowRight className="w-3.5 h-3.5 rotate-180" />
              Recursos
            </Link>
          </div>
        </div>
        
        {/* ===== HEADER COMPACTO ===== */}
        <section className="relative bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 py-8 sm:py-12">
            {/* Badge descriptor */}
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">
              Herramienta gratuita
            </p>
            
            {/* Título principal */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight leading-tight text-slate-900">
              Simulador para Validar tu Startup
            </h1>
            
            {/* Descripción breve */}
            <p className="text-lg sm:text-xl text-slate-700 leading-relaxed mb-6 max-w-3xl">
              Herramienta interactiva que te guía paso a paso en la validación de tu idea siguiendo metodología Lean Startup. Descubre qué validar primero, define experimentos MVP y evita construir sin demanda real.
            </p>

            <div className="flex items-center gap-3 mb-6">
              <a
                href="#simulador"
                className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg text-base"
              >
              Empezar validación ahora
              <ArrowRight className="w-4 h-4" />
            </a>
            <ShareButton
              url="https://edutechpulselab.com/recursos/lean-simulador"
              title="Simulador para Validar tu Startup | Lean Startup"
              text="Herramienta gratuita para validar tu startup con metodología Lean. Descubre qué validar primero."
              className="border-slate-300/60 text-slate-500 hover:text-slate-700 hover:border-slate-400"
            />
            </div>

            {/* Nota de auto-guardado */}
            <p className="text-sm text-slate-600 mt-6">
              <CheckCircle className="w-4 h-4 inline-block mr-1.5 text-emerald-600" />
              Tu progreso se guarda automáticamente en tu navegador
            </p>
          </div>
        </section>

        {/* ===== SIMULADOR - Inmediato ===== */}
        <section id="simulador" className="scroll-mt-20 py-12 sm:py-16">
          <LeanFlowSimulator />
        </section>

        {/* ===== METODOLOGÍA Y BENEFITS ===== */}
        <section className="max-w-7xl mx-auto px-6 sm:px-8 py-12 sm:py-16 bg-slate-50">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 px-8 sm:px-12 py-10 sm:py-14">
            
            {/* Introducción metodología */}
            <div className="mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Cómo funciona este simulador
              </h2>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-4">
                Basado en <strong>"The Lean Startup"</strong> de Eric Ries y <strong>"Running Lean"</strong> de Ash Maurya, este simulador te ayuda a aplicar el ciclo BUILD-MEASURE-LEARN de forma estructurada.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                No es un curso teórico, es una herramienta práctica que te hace las preguntas correctas en el orden correcto para evitar sesgos de confirmación y validar hipótesis críticas antes de invertir tiempo y dinero.
              </p>
            </div>

            {/* Key Benefits - Sin emoticones */}
            <div className="mb-12">
              <h3 className="text-xl font-bold text-slate-900 mb-6">
                Características principales
              </h3>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-cyan-100 flex items-center justify-center">
                    <Target className="w-6 h-6 text-cyan-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-slate-900 mb-2">Flujo guiado</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Preguntas secuenciales diseñadas para evitar sesgos de confirmación. El sistema adapta recomendaciones según tu contexto.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                    <FlaskConical className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-slate-900 mb-2">Experimentos concretos</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Sugerencias específicas de MVPs, smoke tests y experimentos de validación con métricas cuantificables.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-blue-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-slate-900 mb-2">Métricas y umbrales</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Define indicadores de validación realistas (tasas de conversión, % adopción, NPS mínimo) antes de invertir en desarrollo.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-orange-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-slate-900 mb-2">Antipatrones</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Detecta señales de alerta comunes: validación con amigos, confundir interés con intención de compra, optimizar demasiado pronto.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Para quién es */}
            <div className="mb-12">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                ¿Para quién es esta herramienta?
              </h3>
              <ul className="space-y-2 text-base text-slate-700">
                <li className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-600 mt-2 flex-shrink-0"></span>
                  <span><strong>Founders pre-producto:</strong> Validar hipótesis críticas antes de escribir código</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-600 mt-2 flex-shrink-0"></span>
                  <span><strong>Equipos MVP:</strong> Estructurar experimentos con métricas claras</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-600 mt-2 flex-shrink-0"></span>
                  <span><strong>Mentores/Aceleradoras:</strong> Guiar a emprendedores con framework validado</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-600 mt-2 flex-shrink-0"></span>
                  <span><strong>Equipos de innovación corporativa:</strong> Validar ideas internas con rigor</span>
                </li>
              </ul>
            </div>

            {/* Referencias */}
            <div className="border-t border-slate-200 pt-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4">
                Metodología basada en
              </h3>
              <div className="space-y-3">
                <div className="flex gap-3 text-sm text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-600 mt-2 flex-shrink-0"></span>
                  <span>
                    <strong className="text-slate-900">"The Lean Startup"</strong> por Eric Ries — Ciclo BUILD-MEASURE-LEARN y validación continua
                  </span>
                </div>
                <div className="flex gap-3 text-sm text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-600 mt-2 flex-shrink-0"></span>
                  <span>
                    <strong className="text-slate-900">"Running Lean"</strong> por Ash Maurya — Lean Canvas y priorización de hipótesis críticas
                  </span>
                </div>
                <div className="flex gap-3 text-sm text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-600 mt-2 flex-shrink-0"></span>
                  <span>
                    <strong className="text-slate-900">"The Mom Test"</strong> por Rob Fitzpatrick — Técnicas de entrevista para validación sin sesgos
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== CARD PROGRAMA COMPLEMENTARIO - Al final ===== */}
        <section className="max-w-7xl mx-auto px-6 sm:px-8 py-12 sm:py-16">
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-2xl p-8 sm:p-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
              Complementa tu validación con criterio estratégico
            </h2>
            <p className="text-base sm:text-lg text-slate-700 mb-6 leading-relaxed max-w-3xl">
              Validar una startup no es solo recopilar datos, sino <strong>interpretarlos correctamente</strong>. Nuestro programa te ayuda a distinguir señales de ruido, reconocer sesgos cognitivos y tomar decisiones confiables cuando la información es incompleta.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center mb-6">
              <Link
                to="/p/decidir-incertidumbre-15"
                className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-colors shadow-md hover:shadow-lg"
              >
                Explorar programa: Decidir con criterio en incertidumbre
                <ArrowRight className="w-4 h-4" />
              </Link>
              
              <div className="flex flex-wrap gap-2 text-xs sm:text-sm text-slate-600">
                <span className="bg-white px-3 py-1.5 rounded-full border border-slate-200">
                  15 min en audio
                </span>
                <span className="bg-white px-3 py-1.5 rounded-full border border-slate-200">
                  Kahneman, Taleb, Tetlock
                </span>
              </div>
            </div>

            <p className="text-sm text-slate-600">
              Micro-programa gratuito sobre modelos mentales aplicados a toma de decisiones en contextos de incertidumbre.
            </p>
          </div>
        </section>

      </main>
    </>
  );
}
